import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MDRBadge } from "@/components/MDRBadge";
import { toast } from "sonner";
import { consumablesData } from "@/data/platformData";
import {
  Boxes, Plus, Pencil, ArrowLeft, AlertTriangle, RefreshCw
} from "lucide-react";

const PHARMACY_ID = "ph1";

export const ManagerInventoryPage = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState(
    consumablesData.filter(c => c.pharmacyId === PHARMACY_ID)
  );
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const startEdit = (i) => {
    setEditing(i ? { ...i } : { id: `cons${Date.now()}`, name: "", pharmacyId: PHARMACY_ID, stock: 0, threshold: 50, unit: "pz", lastRestock: new Date().toISOString().slice(0, 10) });
    setOpen(true);
  };

  const save = () => {
    if (!editing.name) { toast.error("Indica il nome del consumabile"); return; }
    setItems(prev => {
      const exists = prev.find(p => p.id === editing.id);
      if (exists) return prev.map(p => p.id === editing.id ? editing : p);
      return [...prev, editing];
    });
    setOpen(false);
    toast.success("Consumabile salvato");
  };

  const restock = (id) => {
    setItems(prev => prev.map(p => p.id === id ? { ...p, stock: p.stock + p.threshold, lastRestock: new Date().toISOString().slice(0, 10) } : p));
    toast.success("Riordino registrato");
  };

  const lowCount = items.filter(s => s.stock < s.threshold).length;

  return (
    <div className="p-4 md:p-8 max-w-5xl mx-auto space-y-6">
      <div>
        <Button variant="ghost" size="sm" className="mb-2 -ml-2" onClick={() => navigate("/manager")}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Panoramica
        </Button>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Boxes className="h-4 w-4 text-rose-600" />
              <span className="text-xs uppercase tracking-wider text-muted-foreground">Magazzino</span>
              <MDRBadge variant="admin" size="sm" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Scorte e consumabili</h1>
            <p className="text-sm text-muted-foreground mt-1">
              {items.length} articoli · {lowCount > 0 ? <span className="text-rose-600 font-medium">{lowCount} sotto soglia</span> : "tutti i livelli OK"}
            </p>
          </div>
          <Button variant="premium" onClick={() => startEdit(null)} className="gap-2" data-testid="inventory-new-btn">
            <Plus className="h-4 w-4" /> Nuovo consumabile
          </Button>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        {items.map(s => {
          const isLow = s.stock < s.threshold;
          const ratio = Math.min(100, Math.round((s.stock / Math.max(1, s.threshold * 2)) * 100));
          return (
            <Card key={s.id} className={isLow ? "border-rose-200" : ""}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-2">
                  <CardTitle className="text-base truncate">{s.name}</CardTitle>
                  {isLow && <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200 gap-1"><AlertTriangle className="h-3 w-3" /> Basso</Badge>}
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-baseline justify-between">
                  <p className="text-3xl font-bold">{s.stock}<span className="text-sm font-normal text-muted-foreground ml-1">{s.unit}</span></p>
                  <p className="text-xs text-muted-foreground">Soglia {s.threshold}</p>
                </div>
                <div className="w-full h-2 rounded-full bg-muted overflow-hidden">
                  <div className={`h-full ${isLow ? "bg-rose-500" : "bg-emerald-500"}`} style={{ width: `${ratio}%` }} />
                </div>
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>Riordino {s.lastRestock}</span>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" className="gap-1 h-7" onClick={() => restock(s.id)} data-testid={`inventory-restock-${s.id}`}>
                      <RefreshCw className="h-3 w-3" /> Riordina
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => startEdit(s)}>
                      <Pencil className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing && items.find(i => i.id === editing.id) ? "Modifica consumabile" : "Nuovo consumabile"}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <Label>Nome</Label>
                <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="Es. Elettrodi ECG" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Giacenza</Label>
                  <Input type="number" value={editing.stock} onChange={(e) => setEditing({ ...editing, stock: Number(e.target.value) })} />
                </div>
                <div>
                  <Label>Soglia</Label>
                  <Input type="number" value={editing.threshold} onChange={(e) => setEditing({ ...editing, threshold: Number(e.target.value) })} />
                </div>
                <div>
                  <Label>Unità</Label>
                  <Input value={editing.unit} onChange={(e) => setEditing({ ...editing, unit: e.target.value })} placeholder="pz / kit" />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={save}>Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ManagerInventoryPage;
